import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Card,
  Stack,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Box,
  Chip,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AddPhotoIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { colors } from "../../utils";
import MessageBubble from "./MessageBubble";
import { fileToDataUrl } from "./helpers";
import {
  GREETING,
  EXAMPLE_QUESTIONS,
  MAX_IMAGES,
  MAX_QUESTION_LENGTH,
  SUPPORT_EMAIL,
} from "./constants";

/**
 * The help chat surface. Owns the composer (input text + screenshot
 * attachments); the container owns the thread, the mutation and retry.
 * Props: messages[], pending (bool), error ({kind}|null), onSend(q, images[]),
 * onRetry(), onClose().
 */
export default function HelpPanel({
  messages = [],
  pending = false,
  error = null,
  onSend = () => {},
  onRetry = () => {},
  onClose = () => {},
}) {
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]); // {name, dataUrl}
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const textRef = useRef(null);

  const isEmpty = messages.length === 0;
  const canSend = input.trim() !== "" && !pending;

  // Auto-scroll to the latest turn / indicator.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending, error]);

  // Focus the composer when the panel opens.
  useEffect(() => {
    const t = setTimeout(() => textRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const handleFileSelection = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const remaining = MAX_IMAGES - attachments.length;
    if (remaining <= 0) {
      toast.info(`You can attach up to ${MAX_IMAGES} screenshots.`);
      return;
    }
    const accepted = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Only ${MAX_IMAGES} screenshots can be attached.`);
    }
    try {
      const converted = await Promise.all(
        accepted.map(async (f) => ({ name: f.name, dataUrl: await fileToDataUrl(f) }))
      );
      setAttachments((prev) => [...prev, ...converted].slice(0, MAX_IMAGES));
    } catch (err) {
      toast.error("Couldn't read that image. Please try another.");
    }
  };

  const removeAttachment = (index) =>
    setAttachments((prev) => prev.filter((_, i) => i !== index));

  const submit = () => {
    if (!canSend) return;
    const question = input.trim().slice(0, MAX_QUESTION_LENGTH);
    onSend(question, attachments.map((a) => a.dataUrl));
    setInput("");
    setAttachments([]);
  };

  const askExample = (q) => {
    if (pending) return;
    onSend(q, []);
  };

  return (
    <Card
      role="dialog"
      aria-label="iYuga Help Assistant"
      sx={{
        width: { xs: "calc(100vw - 32px)", sm: 380 },
        maxWidth: 380,
        height: { xs: "70vh", sm: 520 },
        maxHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: `1px solid ${colors.primary}`,
        boxShadow: "0 12px 32px rgba(18,48,81,0.22)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.75,
          py: 1.25,
          backgroundColor: colors.primary,
          color: colors.white,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: colors.PrimaryPurple300 }} />
          <Stack>
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>
              Help Assistant
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.8, lineHeight: 1.2 }}>
              Answers grounded in iYuga help
            </Typography>
          </Stack>
        </Stack>
        <IconButton
          size="small"
          aria-label="Close help assistant"
          onClick={onClose}
          sx={{ color: colors.white }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      {/* Message list */}
      <Box
        ref={scrollRef}
        aria-live="polite"
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.5,
          py: 1.5,
          backgroundColor: colors.white,
        }}
      >
        {isEmpty ? (
          // Empty / first-run state
          <Stack gap={1.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "12px 12px 12px 2px",
                backgroundColor: colors.Highlight,
                border: `1px solid ${colors.PrimaryPurple100}`,
              }}
            >
              <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: colors.primary }}>
                {GREETING}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: colors.darkGrey, mt: 0.5 }}>
              Try asking:
            </Typography>
            <Stack gap={0.75}>
              {EXAMPLE_QUESTIONS.map((q) => (
                <Chip
                  key={q}
                  label={q}
                  onClick={() => askExample(q)}
                  clickable
                  sx={{
                    justifyContent: "flex-start",
                    height: "auto",
                    py: 0.75,
                    borderRadius: "8px",
                    backgroundColor: colors.white,
                    border: `1px solid ${colors.PrimaryPurple300}`,
                    color: colors.primary,
                    fontSize: 12,
                    "& .MuiChip-label": { whiteSpace: "normal" },
                    "&:hover": { backgroundColor: colors.PrimaryPurple100 },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack gap={1.25}>
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                role={m.role}
                content={m.content}
                images={m.images}
                sources={m.sources}
              />
            ))}

            {/* Loading (answer pending) */}
            {pending && (
              <Stack direction="row" alignItems="center" gap={1} sx={{ pl: 0.5 }}>
                <CircularProgress size={14} disableShrink />
                <Typography sx={{ fontSize: 12, color: colors.darkGrey }}>
                  Looking through the help guides…
                </Typography>
              </Stack>
            )}

            {/* Error state */}
            {error && !pending && (
              <Stack
                sx={{
                  p: 1.25,
                  borderRadius: "8px",
                  backgroundColor: colors.yellowLight,
                  border: `1px solid ${colors.yellowDark}`,
                }}
                gap={0.75}
              >
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: colors.yellowText }} />
                  <Typography sx={{ fontSize: 12.5, color: colors.primary, fontWeight: 600 }}>
                    {error.kind === "unavailable"
                      ? "The help assistant isn't available right now."
                      : "Something went wrong."}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, color: colors.darkGrey }}>
                  {error.kind === "unavailable" ? (
                    <>
                      Please try again later, or email{" "}
                      <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.PrimaryPurple700, fontWeight: 600 }}>
                        {SUPPORT_EMAIL}
                      </Link>
                      .
                    </>
                  ) : (
                    <>
                      Please try again. If it keeps happening, email{" "}
                      <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.PrimaryPurple700, fontWeight: 600 }}>
                        {SUPPORT_EMAIL}
                      </Link>
                      .
                    </>
                  )}
                </Typography>
                {error.kind !== "unavailable" && (
                  <Chip
                    icon={<RefreshIcon sx={{ fontSize: 15 }} />}
                    label="Retry"
                    size="small"
                    onClick={onRetry}
                    clickable
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: colors.white,
                      border: `1px solid ${colors.primary}`,
                      color: colors.primary,
                      "& .MuiChip-icon": { color: colors.primary },
                    }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      {/* Attachment thumbnails */}
      {attachments.length > 0 && (
        <Stack
          direction="row"
          gap={0.75}
          sx={{
            px: 1.5,
            py: 1,
            flexWrap: "wrap",
            borderTop: `1px solid ${colors.lightBorder}`,
            backgroundColor: colors.background,
            flexShrink: 0,
          }}
        >
          {attachments.map((a, i) => (
            <Box key={i} sx={{ position: "relative" }}>
              <Box
                component="img"
                src={a.dataUrl}
                alt={a.name}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "6px",
                  objectFit: "cover",
                  border: `1px solid ${colors.lightBorder}`,
                }}
              />
              <IconButton
                size="small"
                aria-label={`Remove ${a.name}`}
                onClick={() => removeAttachment(i)}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  width: 18,
                  height: 18,
                  backgroundColor: colors.primary,
                  color: colors.white,
                  "&:hover": { backgroundColor: colors.PrimaryPurple700 },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* Composer */}
      <Stack
        direction="row"
        alignItems="flex-end"
        gap={0.5}
        sx={{
          px: 1,
          py: 1,
          borderTop: `1px solid ${colors.lightBorder}`,
          backgroundColor: colors.background,
          flexShrink: 0,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelection}
          style={{ display: "none" }}
          aria-hidden="true"
          tabIndex={-1}
        />
        <IconButton
          aria-label="Attach a screenshot"
          onClick={() => fileInputRef.current?.click()}
          disabled={pending || attachments.length >= MAX_IMAGES}
          sx={{ color: colors.primary, width: 40, height: 40 }}
        >
          <AddPhotoIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <TextField
          inputRef={textRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask how to do something…"
          multiline
          maxRows={4}
          inputProps={{ maxLength: MAX_QUESTION_LENGTH, "aria-label": "Your question" }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: colors.white,
              fontSize: 13,
              "& fieldset": { borderColor: colors.lightBorder },
              "&:hover fieldset": { borderColor: colors.PrimaryPurple300 },
              // ISP-9999 (fix-kelly-discount-code-purple-focus-glow): this
              // used to repaint the fieldset the full brand purple on
              // &.Mui-focused -- a second, bigger purple box around the
              // question field, disjoint from the ring the app-wide focus
              // sheet already paints on the <input> itself
              // (src/styles/focusRing.css beats MUI's default outline via
              // specificity). Pinned to the resting border so the shared
              // ring stays the ONE indicator; see
              // src/theme/focusRingHardcodeRatchet.test.js for the
              // app-wide guard.
              "&.Mui-focused fieldset": { borderColor: colors.lightBorder },
            },
          }}
        />
        <IconButton
          aria-label="Send question"
          onClick={submit}
          disabled={!canSend}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: canSend ? colors.primary : colors.disabled,
            color: canSend ? colors.white : colors.mediumGrey,
            "&:hover": { backgroundColor: canSend ? colors.PrimaryPurple700 : colors.disabled },
            "&.Mui-disabled": { backgroundColor: colors.disabled, color: colors.mediumGrey },
          }}
        >
          {pending ? (
            <CircularProgress size={16} sx={{ color: colors.mediumGrey }} disableShrink />
          ) : (
            <SendIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Stack>
    </Card>
  );
}
